import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  IconButton,
  Modal,
  Button,
  Tooltip,
  Checkbox,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  fetchAllMeteringPoints,
  fetchGOCertificates,
  fetchLastGoSyncTime,
} from "store/appSlice";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectGOCertificates, selectLastGoSyncTime } from "store/selectors";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { grey } from "@mui/material/colors";

import { AttachFile } from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import MainLayout from "layouts/Main";
import Table from "components/Table";
import Filters from "./Filters";
import DetailedViewModal from "./DetailedViewModal";
import TransferModal from "./TransferModal";
import UnsyncedClientsModal from "./UnsyncedClientsModal";
import {
  exportGOCertificates,
  getUnsyncedGOClients,
  syncGOs,
} from "api/resources/go";
import { downloadXLSX } from "helpers/excelExport";

interface CertificateRow {
  row: {
    original: any;
  };
}

const PAGE_SIZE_OPTIONS = [25, 50, 100, 500, 1000];

const GuaranteesOfOriginCertificates = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [filters, setFilters] = useState<{
    [key: string]: string | string[];
  }>({});
  const [resetPage, setResetPage] = useState(false);
  const [sortParam, setSortParam] = useState<string | undefined>(undefined);
  const [openDetailedView, setOpenDetailedView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [openTransferModal, setOpenTransferModal] = useState(false);
  const [openUnsyncedModal, setOpenUnsyncedModal] = useState(false);
  const [unsyncedClients, setUnsyncedClients] = useState<
    { id: number; name: string }[]
  >([]);
  const [unsyncedLoading, setUnsyncedLoading] = useState(true);
  const [unsyncedError, setUnsyncedError] = useState<string | null>(null);
  const GOCertificates = useAppSelector(selectGOCertificates);
  const lastGoSyncTime = useAppSelector(selectLastGoSyncTime);

  const [snackbar, setSnackbar] = useState(null);

  const dispatch = useAppDispatch();

  useEffect(() => {
    setCurrentPage(0);
    setResetPage(true);
  }, [filters, pageSize]);

  useEffect(() => {
    setUnsyncedLoading(true);
    setUnsyncedError(null);
    getUnsyncedGOClients()
      .then((result) => {
        setUnsyncedClients(result?.data ?? []);
      })
      .catch(() => {
        setUnsyncedError("Failed to load unsynced clients");
      })
      .finally(() => {
        setUnsyncedLoading(false);
      });
  }, []);

  useEffect(() => {
    // Filter out "All" values, null, undefined, and empty strings
    const processedFilters = Object.entries(filters).reduce(
      (acc, [key, value]) => {
        if (Array.isArray(value)) {
          if (value.length > 0) acc[key] = value;
          return acc;
        }
        if (
          value === "All" ||
          value === "all" ||
          value === "" ||
          value === null ||
          value === undefined
        ) {
          return acc;
        }
        acc[key] = value;
        return acc;
      },
      {} as { [key: string]: string | string[] },
    );

    const allFilters = sortParam
      ? { ...processedFilters, sort: sortParam }
      : processedFilters;
    dispatch(fetchGOCertificates(currentPage, pageSize, allFilters));
    dispatch(fetchLastGoSyncTime());
  }, [currentPage, pageSize, filters, sortParam, dispatch]);

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortParam(`${field},${direction}`);
  };

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        setCurrentPage(0);
        setResetPage(true);
        dispatch(
          fetchAllMeteringPoints(
            currentPage,
            25,
            filters as { [key: string]: string },
          ),
        );
      }
    };

    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [filters, dispatch]);

  const snackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar(null);
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      // Process filters exactly the same way as in useEffect for consistency
      const processedFilters = Object.entries(filters).reduce(
        (acc, [key, value]) => {
          if (Array.isArray(value)) {
            if (value.length > 0) acc[key] = value;
            return acc;
          }
          if (
            value === "All" ||
            value === "all" ||
            value === "" ||
            value === null ||
            value === undefined
          ) {
            return acc;
          }
          acc[key] = value;
          return acc;
        },
        {} as { [key: string]: string | string[] },
      );

      // Include sort parameter if present, same as fetchGOCertificates
      const allFilters = sortParam
        ? { ...processedFilters, sort: sortParam }
        : processedFilters;

      const data = await exportGOCertificates(allFilters);
      const filename = `GO_Certificates_${dayjs().format("YYYY-MM-DD_HH-mm")}.xlsx`;
      await downloadXLSX(data, filename);
    } catch (error) {
      console.error("Error exporting certificates:", error);
      setSnackbar({
        type: "error",
        message: "Failed to export certificates",
      });
    } finally {
      setLoading(false);
    }
  };

  const certificateRows: any[] = GOCertificates?.content || [];

  const selectedCertificates = certificateRows.filter((cert: any) =>
    selectedRows.includes(cert.id),
  );

  const pageIds = certificateRows.map((cert) => cert.id);
  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedRows.includes(id));
  const somePageSelected =
    pageIds.some((id) => selectedRows.includes(id)) && !allPageSelected;

  const toggleRow = (id: number) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleAllOnPage = () => {
    if (allPageSelected) {
      setSelectedRows((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedRows((prev) =>
        Array.from(new Set([...prev, ...pageIds])),
      );
    }
  };

  const handleTransferSuccess = () => {
    setSnackbar({
      type: "success",
      message: "Certificates transferred successfully",
    });
    setSelectedRows([]);

    // Process filters the same way as in useEffect
    const processedFilters = Object.entries(filters).reduce(
      (acc, [key, value]) => {
        if (Array.isArray(value)) {
          if (value.length > 0) acc[key] = value;
          return acc;
        }
        if (
          value === "All" ||
          value === "all" ||
          value === "" ||
          value === null ||
          value === undefined
        ) {
          return acc;
        }
        acc[key] = value;
        return acc;
      },
      {} as { [key: string]: string | string[] },
    );

    const allFilters = sortParam
      ? { ...processedFilters, sort: sortParam }
      : processedFilters;

    dispatch(fetchGOCertificates(currentPage, pageSize, allFilters));
  };

  const handleSyncGOs = () => {
    syncGOs().catch((error) => console.error("GO sync failed:", error));
    setSnackbar({
      type: "info",
      message: "GO sync started. This may take a few minutes.",
    });
  };

  const handleTransferError = (message: string) => {
    setSnackbar({
      type: "error",
      message: message,
    });
  };

  const columns = [
    {
      id: "select",
      header: () => (
        <Checkbox
          size="small"
          checked={allPageSelected}
          indeterminate={somePageSelected}
          onChange={toggleAllOnPage}
        />
      ),
      cell: (info: CertificateRow) => (
        <Checkbox
          size="small"
          checked={selectedRows.includes(info.row.original.id)}
          onChange={() => toggleRow(info.row.original.id)}
        />
      ),
      enableSorting: false,
    },
    {
      accessorKey: "client",
      header: "Client",
      cell: (info: CertificateRow) => (
        <Box sx={{ whiteSpace: "nowrap" }}>
          {info.row.original.client.name}
        </Box>
      ),
    },
    {
      accessorKey: "technology",
      header: "Technology",
      cell: (info: CertificateRow) => (
        <Box display="flex" flexDirection="column">
          <Typography component="p" fontSize="0.8rem">
            {info.row.original.technology.name}
          </Typography>
          <Typography component="p" color={grey[600]} fontSize="0.7rem">
            {info.row.original.fuel.name}
          </Typography>
        </Box>
      ),
    },
    {
      accessorKey: "production",
      header: "Production period",
      enableSorting: false,
      cell: (info: CertificateRow) => (
        <Box sx={{ whiteSpace: "nowrap" }}>
          {dayjs
            .utc(info.row.original.productionStart)
            .tz("Europe/Tallinn")
            .format("DD.MM.YYYY")}
          {" - "}
          {dayjs
            .utc(info.row.original.productionEnd)
            .tz("Europe/Tallinn")
            .format("DD.MM.YYYY")}
        </Box>
      ),
    },
    {
      accessorKey: "transferDeadline",
      header: "Transfer deadline",
      cell: (info: CertificateRow) =>
        info.row.original.transferDeadline
          ? dayjs(info.row.original.transferDeadline).format("DD.MM.YYYY")
          : "-",
    },
    {
      accessorKey: "expiryDate",
      header: "Expiry date",
      cell: (info: CertificateRow) =>
        dayjs(info.row.original.expiryDate).format("DD.MM.YYYY"),
    },
    {
      accessorKey: "productionSupport",
      header: "Production",
      cell: (info: CertificateRow) =>
        info.row.original.productionSupport ? (
          <CheckIcon fontSize="small" color="success" />
        ) : (
          <CloseIcon fontSize="small" color="error" />
        ),
    },
    {
      accessorKey: "investmentSupport",
      header: "Investment",
      cell: (info: CertificateRow) =>
        info.row.original.investmentSupport ? (
          <CheckIcon fontSize="small" color="success" />
        ) : (
          <CloseIcon fontSize="small" color="error" />
        ),
    },
    {
      accessorKey: "amount",
      header: "Amount",
    },
    {
      id: "viewMore",
      header: "",
      enableSorting: false,
      cell: (info: CertificateRow) => (
        <IconButton
          color="primary"
          size="small"
          onClick={() => setOpenDetailedView(info.row.original)}
        >
          <ArrowForwardIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <MainLayout
      tabs={[
        { label: "Summary", path: "GuaranteesOfOriginSummary" },
        { label: "Certificates", path: "GuaranteesOfOriginCertificates" },
      ]}
      snackbar={{
        open: snackbar,
        handleClose: snackbarClose,
        type: snackbar?.type,
        message: snackbar?.message,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-end">
        <Box>
          <Typography variant="body2" color="textSecondary">
            Guarantees of Origin
          </Typography>
          <Typography variant="h5">Certificates</Typography>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="flex-end"
        >
          <Box display="flex" alignItems="center" sx={{ mb: "0.4rem" }}>
            <Typography variant="body2" color="textSecondary">
              {lastGoSyncTime
                ? `Last updated: ${dayjs(
                    lastGoSyncTime.lastSyncFinishedAt + "Z",
                  ).format("DD.MM.YYYY [at] HH:mm")}`
                : "Last updated: -"}
            </Typography>
            <Tooltip title="Sync GO-s">
              <IconButton size="small" onClick={handleSyncGOs} sx={{ ml: 0.5 }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              size="small"
              startIcon={<SwapHorizIcon />}
              onClick={() => setOpenTransferModal(true)}
              disabled={selectedRows.length === 0}
            >
              Transfer ({selectedRows.length})
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ErrorOutlineIcon />}
              onClick={() => setOpenUnsyncedModal(true)}
            >
              Unsynced clients
              {!unsyncedLoading && !unsyncedError
                ? ` (${unsyncedClients.length})`
                : ""}
            </Button>
            <LoadingButton
              loading={loading}
              variant="outlined"
              size="small"
              startIcon={<AttachFile />}
              onClick={handleExport}
            >
              Export certificates
            </LoadingButton>
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: "2rem" }}>
        <Filters filters={filters} setFilters={setFilters} />
        <Table
          data={certificateRows}
          columns={columns}
          isFetching={!GOCertificates}
          pageCount={GOCertificates?.totalPages}
          page={(newPage) => {
            setCurrentPage(newPage);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          resetPage={resetPage}
          onSort={handleSort}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageSizeChange={setPageSize}
          stickyHeader
        />
      </Box>
      <Modal
        open={!!openDetailedView}
        onClose={() => setOpenDetailedView(null)}
      >
        <DetailedViewModal certificate={openDetailedView} />
      </Modal>
      <Modal
        open={openTransferModal}
        onClose={() => setOpenTransferModal(false)}
      >
        <TransferModal
          certificates={selectedCertificates || []}
          onClose={() => setOpenTransferModal(false)}
          onSuccess={handleTransferSuccess}
          onError={handleTransferError}
        />
      </Modal>
      <Modal
        open={openUnsyncedModal}
        onClose={() => setOpenUnsyncedModal(false)}
      >
        <UnsyncedClientsModal
          clients={unsyncedClients}
          loading={unsyncedLoading}
          error={unsyncedError}
          onClose={() => setOpenUnsyncedModal(false)}
        />
      </Modal>
    </MainLayout>
  );
};

export default GuaranteesOfOriginCertificates;
